// src/context/BookmarkContext.jsx
// Fetches all of the current user's bookmarks once on login.
// Stores as a Set of entry IDs for O(1) lookup.
// All components read from this — zero per-panel API calls needed.

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';

const BookmarkContext = createContext({
  bookmarkedIds:  new Set(),
  addBookmark:    () => {},
  removeBookmark: () => {},
  isBookmarked:   () => false,
  loading:        false,
});

export function BookmarkProvider({ children }) {
  const { user } = useAuth();
  const [bookmarkedIds, setBookmarkedIds] = useState(new Set());
  const [loading, setLoading]             = useState(false);

  // Fetch all bookmarks once when user logs in
  useEffect(() => {
    if (!user?.id) {
      setBookmarkedIds(new Set());
      return;
    }
    setLoading(true);
    supabase
      .from('bookmarks')
      .select('entry_id')
      .eq('user_id', user.id)
      .then(({ data }) => {
        setBookmarkedIds(new Set((data || []).map(b => b.entry_id)));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [user?.id]);

  const addBookmark = useCallback((entryId) => {
    setBookmarkedIds(prev => new Set([...prev, entryId]));
  }, []);

  const removeBookmark = useCallback((entryId) => {
    setBookmarkedIds(prev => {
      const next = new Set(prev);
      next.delete(entryId);
      return next;
    });
  }, []);

  const isBookmarked = useCallback((entryId) => {
    return bookmarkedIds.has(entryId);
  }, [bookmarkedIds]);

  return (
    <BookmarkContext.Provider value={{ bookmarkedIds, addBookmark, removeBookmark, isBookmarked, loading }}>
      {children}
    </BookmarkContext.Provider>
  );
}

export function useBookmarks() {
  return useContext(BookmarkContext);
}
